import { inject, Injector } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { Router, type CanActivateFn, type UrlTree } from "@angular/router";
import { filter, firstValueFrom } from "rxjs";

import { OrganizationFeaturesService } from "../services/organization-features.service";

import type { OrganizationFeature } from "../models/organization-feature";

/**
 * An optional page — Activity or Documents — opens only while the
 * organization has it switched on; otherwise the dashboard opens instead.
 *
 * Waits for the organization's own answer rather than the all-on default, so
 * a bookmarked /activity cannot slip through in the moment before it arrives.
 * A UI affordance, like the sidebar: the data behind the page is scoped to the
 * caller's customer by the API whether the page is on or off.
 */
export const featureGuard = (feature: OrganizationFeature): CanActivateFn =>
  async (): Promise<boolean | UrlTree> => {
    const features = inject(OrganizationFeaturesService);
    const router = inject(Router);
    const injector = inject(Injector);

    if (!features.settled()) {
      await firstValueFrom(
        toObservable(features.settled, { injector }).pipe(filter((settled: boolean) => settled))
      );
    }

    return features.isOn(feature) ? true : router.createUrlTree(["/dashboard"]);
  };
